import React, { useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import Icon from 'common/components/Icon';
import Typography from 'common/components/Typography';
import Button from 'common/components/Button';
import colors from 'common/styles/colors';

import { XMN_DECIMALS } from '../../../formatting';
import AmountInput from './AmountInput';
import ErrorBanner from './ErrorBanner';
import styles from './AmountInputStep.module.css';

const AmountInputStep = ({
  euroAmount,
  xmnRate,
  xmnBalance,
  euroBalance,
  isSubmitting,
  error,
  onAmountChange,
  onSubmit,
}) => {
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const numericEuroAmount = useMemo(
    () => parseFloat(euroAmount) || 0,
    [euroAmount]
  );

  const validationError = useMemo(() => {
    if (euroAmount === '' || numericEuroAmount === 0) {
      return 'Please enter an amount';
    }
    if (numericEuroAmount < 0) {
      return 'Amount cannot be negative';
    }
    const maxAmount = parseFloat(euroBalance.toFixed(XMN_DECIMALS));
    if (parseFloat(numericEuroAmount.toFixed(XMN_DECIMALS)) > maxAmount) {
      return 'Amount exceeds available balance';
    }
    return null;
  }, [euroAmount, numericEuroAmount, euroBalance]);

  const isFormValid = validationError === null;

  const handleSubmit = useCallback(() => {
    setHasAttemptedSubmit(true);
    if (isFormValid) {
      onSubmit();
    }
  }, [isFormValid, onSubmit]);

  return (
    <div className={styles.form}>
      <div className={styles.infoSection}>
        <div className={styles.infoHeader}>
          <Icon.DollarCircle
            description="Amount"
            size="medium"
            width={20}
            height={20}
            fill={colors['rebranding-md-color-brand-purple']}
          />
          <Typography
            tag="span"
            size="base"
            weight="semiBold"
            color="color-neutral-900"
          >
            Amount to withdraw
          </Typography>
        </div>
        <Typography tag="p" size="xs" color="color-neutral-500">
          Choose how much you want to receive in the provided address.
        </Typography>
      </div>

      <AmountInput
        euroAmount={euroAmount}
        xmnRate={xmnRate}
        xmnBalance={xmnBalance}
        euroBalance={euroBalance}
        validationError={validationError}
        hasAttemptedSubmit={hasAttemptedSubmit}
        onAmountChange={onAmountChange}
        onSubmit={handleSubmit}
      />
      {error && (
        <ErrorBanner
          message="Something went wrong. Please try again."
          data-test="withdraw-amount-error"
        />
      )}
      <Button kind="primary" full onClick={handleSubmit} disabled={isSubmitting}>
        <button type="button" disabled={isSubmitting} data-test="withdraw-submit-button">
          {isSubmitting ? 'Submitting...' : 'Withdraw rewards'}
        </button>
      </Button>
    </div>
  );
};

AmountInputStep.propTypes = {
  euroAmount: PropTypes.string.isRequired,
  xmnRate: PropTypes.number.isRequired,
  xmnBalance: PropTypes.number.isRequired,
  euroBalance: PropTypes.number.isRequired,
  isSubmitting: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onAmountChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

AmountInputStep.defaultProps = {
  isSubmitting: false,
  error: null,
};

export default AmountInputStep;
